import { useT } from "@/lib/i18n";

export type TeamMember = {
  slug: string;
  name: string;
  role: string;
  shortRole: string;
  bio: string;
  specialties: string[];
  portrait: { url: string; alt: string };
  /**
   * Generic per-slot media key (team.1.portrait … team.6.portrait). Slots are
   * stable — people change, but slot N keeps its key, so the backoffice never
   * has to migrate when someone joins or leaves.
   */
  portraitKey: string;
  featured: boolean;
};

/**
 * Renders an initials-based SVG avatar (data URL) so we don't ship per-member
 * photos. The backoffice can still override portraits via media keys later.
 */
function initialsAvatar(initials: string, alt: string) {
  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 533" preserveAspectRatio="xMidYMid slice">
  <defs>
    <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="#10182a"/>
      <stop offset="1" stop-color="#1f2b4d"/>
    </linearGradient>
  </defs>
  <rect width="400" height="533" fill="url(#g)"/>
  <text x="50%" y="55%" text-anchor="middle" font-family="'Saira Stencil One', Impact, sans-serif" font-size="180" fill="#f5c518" letter-spacing="4">${initials}</text>
</svg>`;
  return {
    url: `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`,
    alt,
  };
}

export function useTeam(): TeamMember[] {
  const { tk } = useT();

  return [
    {
      slug: "jesus-founder",
      name: tk("team.jesus.name", "Jesus Ibañez"),
      role: tk("team.jesus.role", "Founder & CEO"),
      shortRole: tk("team.jesus.shortRole", "Founder & CEO"),
      bio: tk(
        "team.jesus.bio",
        "Drives the vision and strategy of PULSO33. Two decades of experience designing and shipping mission-critical IT systems for companies of every size."
      ),
      specialties: [
        tk("team.jesus.s1", "Product strategy"),
        tk("team.jesus.s2", "Solution architecture"),
        tk("team.jesus.s3", "Client partnership"),
      ],
      portrait: initialsAvatar("J", "Jesús — Founder & CEO"),
      portraitKey: "team.1.portrait",
      featured: true,
    },
    {
      slug: "engineering-lead",
      name: tk("team.eng.name", "Engineering Team"),
      role: tk("team.eng.role", "Web Applications & APIs"),
      shortRole: tk("team.eng.shortRole", "Engineering"),
      bio: tk(
        "team.eng.bio",
        "Full-stack engineers shipping production-grade web apps, APIs and integrations. TypeScript, React, Node, edge runtimes and modern data stacks."
      ),
      specialties: [
        tk("team.eng.s1", "Web applications"),
        tk("team.eng.s2", "APIs & integrations"),
        tk("team.eng.s3", "Performance"),
      ],
      portrait: initialsAvatar("EN", "Engineering team"),
      portraitKey: "team.2.portrait",
      featured: false,
    },
    {
      slug: "ai-lead",
      name: tk("team.ai.name", "AI & Agents Lab"),
      role: tk("team.ai.role", "AI Agents & LLM Integration"),
      shortRole: tk("team.ai.shortRole", "AI & Agents"),
      bio: tk(
        "team.ai.bio",
        "We build AI agents that actually do work — connected to your tools, your data and your processes. From RAG to autonomous workflows in production."
      ),
      specialties: [
        tk("team.ai.s1", "AI agents"),
        tk("team.ai.s2", "LLM integration"),
        tk("team.ai.s3", "RAG & vector search"),
      ],
      portrait: initialsAvatar("AI", "AI & Agents Lab"),
      portraitKey: "team.3.portrait",
      featured: false,
    },
    {
      slug: "cloud-devops",
      name: tk("team.cloud.name", "Cloud & DevOps"),
      role: tk("team.cloud.role", "Infrastructure, CI/CD & Reliability"),
      shortRole: tk("team.cloud.shortRole", "Cloud & DevOps"),
      bio: tk(
        "team.cloud.bio",
        "Cloud architects building scalable, secure and observable infrastructure. AWS, GCP, Cloudflare, Kubernetes, Terraform and zero-downtime delivery pipelines."
      ),
      specialties: [
        tk("team.cloud.s1", "AWS / GCP / Cloudflare"),
        tk("team.cloud.s2", "Kubernetes & Terraform"),
        tk("team.cloud.s3", "CI/CD & SRE"),
      ],
      portrait: initialsAvatar("☁", "Cloud & DevOps team"),
      portraitKey: "team.4.portrait",
      featured: false,
    },
    {
      slug: "bi-data",
      name: tk("team.bi.name", "Data & BI"),
      role: tk("team.bi.role", "Analytics, Dashboards & Data Platforms"),
      shortRole: tk("team.bi.shortRole", "Data & BI"),
      bio: tk(
        "team.bi.bio",
        "Data engineers and analysts turning raw events into decisions. ETL/ELT pipelines, warehouses, real-time dashboards and metrics that move the business."
      ),
      specialties: [
        tk("team.bi.s1", "ETL & data modeling"),
        tk("team.bi.s2", "Warehouses & lakes"),
        tk("team.bi.s3", "Dashboards & KPIs"),
      ],
      portrait: initialsAvatar("BI", "Data & BI team"),
      portraitKey: "team.5.portrait",
      featured: false,
    },
    {
      slug: "ops",
      name: tk("team.ops.name", "Automation & Ops"),
      role: tk("team.ops.role", "Process Automation & Integrations"),
      shortRole: tk("team.ops.shortRole", "Automation"),
      bio: tk(
        "team.ops.bio",
        "We connect your stack end-to-end: CRM, ERP, messaging, payments, billing. Workflow automation that removes manual work without breaking what already works."
      ),
      specialties: [
        tk("team.ops.s1", "Workflow automation"),
        tk("team.ops.s2", "CRM / ERP integration"),
        tk("team.ops.s3", "Webhooks & events"),
      ],
      portrait: initialsAvatar("OP", "Automation & Ops team"),
      portraitKey: "team.6.portrait",
      featured: false,
    },
  ];
}
